import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mutator } from '@/shared/api';
import { invalidateRoots, QUERY_ROOTS } from '@/shared/api/queryRoots';
import { handleSuccess } from "@/shared/lib/success";
import { productKeys } from "./queryKeys";

interface ProductUpdatePayload {
  id: string;
  payload: Record<string, unknown>;
}
interface ProductVersionPayload {
  id: string;
  [key: string]: unknown;
}

export function useProductCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      mutator("POST", "/products", data),
    onSuccess: (_data, variables) => {
      invalidateRoots(queryClient, QUERY_ROOTS.categories, QUERY_ROOTS.materials, QUERY_ROOTS.featuredSections, QUERY_ROOTS.dashboardHome);
      // Toast is handled by the caller with status-aware copy
      // Only show generic toast when called outside the wizard (no status in payload)
      if (!variables?.status) {
        handleSuccess(_data, { titleKey: "success.product.created" });
      }
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
    },
  });
}

export function useProductPublish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductVersionPayload) =>
      mutator("POST", `/products/version/${data.id}/publish`, data),
    onSuccess: (data, variables) => {
      invalidateRoots(queryClient, QUERY_ROOTS.categories, QUERY_ROOTS.materials, QUERY_ROOTS.featuredSections, QUERY_ROOTS.dashboardHome);
      handleSuccess(data, { titleKey: "success.product.published" });
      // Publish flips the product's status field; the detail page renders
      // that field, so invalidate its cache in addition to the lists.
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: productKeys.detail(variables.id),
      });
    },
  });
}

export function useProductUnpublish() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductVersionPayload) =>
      mutator("POST", `/products/version/${data.id}/unpublish`, data),
    onSuccess: (data, variables) => {
      invalidateRoots(queryClient, QUERY_ROOTS.categories, QUERY_ROOTS.materials, QUERY_ROOTS.featuredSections, QUERY_ROOTS.dashboardHome);
      handleSuccess(data, { titleKey: "success.product.unpublished" });
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: productKeys.detail(variables.id),
      });
    },
  });
}

export function useProductUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductUpdatePayload) =>
      mutator("PUT", `/products/${data.id}`, data.payload),
    onSuccess: (data, variables) => {
      invalidateRoots(queryClient, QUERY_ROOTS.categories, QUERY_ROOTS.materials, QUERY_ROOTS.featuredSections, QUERY_ROOTS.dashboardHome);
      // Suppress when caller provides status-aware toast (wizard, edit page)
      if (!(variables as ProductUpdatePayload)?.payload?.status) {
        handleSuccess(data, { titleKey: "success.product.updated" });
      }
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      // Detail cache also carries the edited fields (title / price / images /
      // description / etc). Without invalidating it, the detail page keeps
      // showing pre-edit data until a hard refresh — the vendor edits a
      // product and their own detail view lies to them.
      queryClient.invalidateQueries({
        queryKey: productKeys.detail(variables.id),
      });
    },
  });
}

export function useProductDelete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutator("DELETE", `/products/${id}`),
    onSuccess: (data, id) => {
      invalidateRoots(queryClient, QUERY_ROOTS.categories, QUERY_ROOTS.materials, QUERY_ROOTS.featuredSections, QUERY_ROOTS.dashboardHome);
      handleSuccess(data, { titleKey: "success.product.deleted" });
      queryClient.invalidateQueries({ queryKey: productKeys.lists() });
      // Purge the deleted product's detail cache entirely — invalidate
      // isn't enough because a subsequent visit to /products/:id would
      // still hydrate from the stale cache before the refetch resolves,
      // and if the vendor lands there fresh, useProductDetails serves the
      // pre-deletion snapshot indefinitely (spinning skeleton in the UI
      // even though the product is gone). removeQueries drops the entry
      // so the next mount starts with `data: undefined` and the detail
      // page can render its own not-found state as soon as the BE 404s.
      queryClient.removeQueries({ queryKey: productKeys.detail(id) });
    },
  });
}
